/**
 * What the analysis says about one file, in the words a customer can act on.
 *
 * The analysis screen measures; this module is what lets every *other* screen
 * report the same measurement without re-deriving it. Before it existed, a
 * user who ran an analysis and returned to their photographs could not tell
 * which ones were the problem - the verdict lived only in the analysis screen's
 * drill-down, and finding a bad photograph meant hunting between two screens.
 *
 * Three rules, and each is a way this could quietly lie:
 *
 * **Unknown is a third state, not a guess.** A file nobody has assessed is
 * `not_assessed`. A file whose assessment could not be *read* is `unknown`.
 * Neither is "İyi" and neither is "Sorunlu". Collapsing them would mean a
 * network failure silently marking a whole session as fine.
 *
 * **The verdict comes from the stored status.** `excellent`/`good`/`usable`/
 * `poor`/`rejected` are grades on the overall score, with boundaries fixed in
 * `domain/quality.py` precisely so they mean the same thing across profiles.
 * The customer words map onto those grades one-for-one; nothing here invents a
 * sixth category or re-scores anything.
 *
 * **An action is offered only where the backend can perform it.** `canEnhance`
 * mirrors the rule in `application/enhancement.py`: enhancement needs a
 * measured, recoverable issue. Offering it otherwise produced the worst kind
 * of dead end - a button that ran, failed, and reported that there was nothing
 * to fix.
 */

import type { MediaAsset, QualityAssessment, QualityStatus } from "@/api/types";
import type { StatusTone } from "@/i18n/terms";

export type MediaAnalysis =
  | { state: "assessed"; assessment: QualityAssessment }
  /** The server answered 404: no assessment has ever been stored. */
  | { state: "not_assessed" }
  /** The assessment could not be read. Never rendered as a verdict. */
  | { state: "unknown" };

export type VerdictKey = "good" | "check" | "recapture" | "unusable" | "not_assessed" | "unknown";

export interface Verdict {
  key: VerdictKey;
  label: string;
  tone: StatusTone;
  /** Never the only carrier of meaning - the label is always present too. */
  glyph: string;
}

/** The customer word for each stored grade. One-for-one, no invention. */
const BY_STATUS: Record<QualityStatus, VerdictKey> = {
  excellent: "good",
  good: "good",
  usable: "check",
  poor: "recapture",
  rejected: "unusable",
};

export const VERDICTS: Record<VerdictKey, Verdict> = {
  good: { key: "good", label: "İyi", tone: "success", glyph: "✓" },
  check: { key: "check", label: "Kontrol et", tone: "caution", glyph: "⚠" },
  recapture: { key: "recapture", label: "Yeniden çekilmesi önerilir", tone: "danger", glyph: "↻" },
  unusable: { key: "unusable", label: "Kullanılamıyor", tone: "danger", glyph: "✕" },
  not_assessed: { key: "not_assessed", label: "Analiz edilmedi", tone: "neutral", glyph: "–" },
  unknown: { key: "unknown", label: "Analiz durumu okunamadı", tone: "neutral", glyph: "?" },
};

export function verdictOf(analysis: MediaAnalysis): Verdict {
  if (analysis.state === "not_assessed") return VERDICTS.not_assessed;
  if (analysis.state === "unknown") return VERDICTS.unknown;
  return VERDICTS[BY_STATUS[analysis.assessment.status]];
}

/** The two verdicts that mean the file should not be relied on as it is. */
export const PROBLEM_STATUSES: QualityStatus[] = ["poor", "rejected"];

export const isProblem = (analysis: MediaAnalysis): boolean =>
  analysis.state === "assessed" && PROBLEM_STATUSES.includes(analysis.assessment.status);

/**
 * Issues the enhancement pipeline has a transformation for.
 *
 * Mirrors `EnhanceMedia.execute`. A mirror is a drift risk, so it is a narrow
 * one: these four codes select `conservative_tonal_normalization` and
 * `deterministic_upscale`, and the backend refuses with 409 when no
 * transformation applies. Erring narrow is safe - the button is hidden on a
 * file that could have been enhanced - while erring wide reproduces the dead
 * end this replaced.
 */
const RECOVERABLE_CODES = ["UNDEREXPOSED", "OVEREXPOSED", "LOW_CONTRAST", "LOW_RESOLUTION"];

/**
 * The sharpness below which blur is not declared recoverable.
 *
 * The same 0.18 as `application/enhancement.py`, where a critical blur is
 * deliberately left alone rather than sharpened into looking fixed. Pinned by
 * `tests/unit/test_enhancement_contract.py` so moving it there fails a test
 * that names this file.
 */
export const BLUR_RECOVERY_SHARPNESS_FLOOR = 0.18;

export function canEnhance(asset: MediaAsset, analysis: MediaAnalysis): boolean {
  if (asset.media_type !== "photo") return false;
  if (analysis.state !== "assessed") return false;
  const codes = new Set((analysis.assessment.issues ?? []).map((issue) => issue.code));
  if (RECOVERABLE_CODES.some((code) => codes.has(code))) return true;
  return (
    codes.has("BLUR") &&
    analysis.assessment.sharpness_score >= BLUR_RECOVERY_SHARPNESS_FLOOR
  );
}

/**
 * Turkish for a measured quality issue code.
 *
 * Composed here from the code, exactly as `i18n/issues.ts` does for coverage,
 * so a backend copy edit cannot change what a user is told and an unknown
 * future code falls back to Turkish rather than leaking English prose.
 */
export function qualityIssueText(code: string): string {
  switch (code) {
    case "BLUR":
      return "Netlik yetersiz";
    case "POSSIBLE_MOTION_BLUR":
      return "Hareket bulanıklığı olabilir";
    case "UNDEREXPOSED":
      return "Yetersiz ışık";
    case "OVEREXPOSED":
      return "Aşırı pozlama";
    case "LOW_CONTRAST":
      return "Düşük kontrast";
    case "LOW_RESOLUTION":
      return "Düşük çözünürlük";
    case "CORRUPT_FRAME":
      return "Dosya okunamadı";
    case "INSUFFICIENT_DETAIL":
      return "Yetersiz detay";
    default:
      // A code this build does not know about. Say that it exists rather than
      // rendering the backend's English explanation.
      return "Ölçülen bir kalite sorunu";
  }
}

/**
 * What a measured issue means, and what to do about it.
 *
 * Only for codes where a change to the capture is the remedy. A code with no
 * entry gets the honest general sentence rather than invented advice: the
 * measurement does not support a specific instruction, and sending someone to
 * adjust the wrong thing is worse than admitting the finding is general.
 */
const ISSUE_GUIDANCE: Record<string, { what: string; next: string }> = {
  BLUR: {
    what: "Fotoğraf bulanık görünüyor.",
    next: "Aynı açıyı, kamerayı sabit tutarak yeniden çekmeniz önerilir.",
  },
  POSSIBLE_MOTION_BLUR: {
    what: "Çekim sırasındaki hareket, görüntüde bulanıklık bırakmış olabilir.",
    next: "Kamerayı daha yavaş hareket ettirerek aynı açıyı tekrar çekin.",
  },
  UNDEREXPOSED: {
    what: "Fotoğraf çok karanlık.",
    next: "Ortam ışığını artırıp aynı açıyı yeniden çekin.",
  },
  OVEREXPOSED: {
    what: "Fotoğraf aşırı aydınlık; parlak alanlarda detay kaybolmuş.",
    next: "Doğrudan güçlü ışıktan kaçınarak aynı açıyı yeniden çekin.",
  },
  LOW_CONTRAST: {
    what: "Konu ile arka plan arasındaki ayrım zayıf.",
    next: "Konuyu daha belirgin bir arka plandan veya farklı ışıkta çekin.",
  },
  LOW_RESOLUTION: {
    what: "Görüntünün piksel boyutu düşük.",
    next: "Daha yüksek çözünürlükte çekin veya konuya yaklaşın.",
  },
  INSUFFICIENT_DETAIL: {
    what: "Görüntüde yüzey detayı az.",
    next: "Konuya yaklaşın veya daha net bir görüntü alın.",
  },
  CORRUPT_FRAME: {
    what: "Dosyanın görüntü verisi okunamadı.",
    next: "Dosyayı kaynağından yeniden yükleyin veya aynı kareyi yeniden çekin.",
  },
};

export function issueGuidance(code: string): { what: string; next: string } {
  return (
    ISSUE_GUIDANCE[code] ?? {
      what: qualityIssueText(code),
      next: "Bu dosyayı yeniden çekmek sonucu iyileştirebilir.",
    }
  );
}
